import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, Trash2, FileDown } from 'lucide-react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';

const STATUS_STYLE = {
  pending: 'border-amber-300 bg-amber-50 text-amber-900',
  completed: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  rejected: 'border-red-300 bg-red-50 text-red-800',
};

/**
 * Compliance groundwork — a patient asked (from the portal) to export or
 * delete their account data. Staff review and resolve each request here;
 * resolving it is written to the PHI audit trail.
 */
export default function PrivacyRequestsPage() {
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState(null);
  const [acting, setActing] = useState(null); // { request, decision }
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get('/compliance/requests', { params: { status } })
      .then((r) => setRows(r.data.requests))
      .catch(() => toast.error('Could not load privacy requests'));
  }, [status]);
  useEffect(() => { setRows(null); load(); }, [load]);

  const resolve = async () => {
    setBusy(true);
    try {
      await api.post(`/compliance/requests/${acting.request.request_id}/complete`,
        { decision: acting.decision, note });
      toast.success(acting.decision === 'completed' ? 'Request completed' : 'Request rejected');
      setActing(null); setNote(''); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not resolve this request'); }
    finally { setBusy(false); }
  };

  const downloadExport = async (requestId) => {
    try {
      const { data } = await api.get(`/compliance/requests/${requestId}/export`);
      const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${requestId}.json`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Export not available'); }
  };

  return (
    <div className="space-y-6" data-testid="privacy-requests-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <ShieldCheck className="h-7 w-7 text-primary" /> Privacy requests
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Patients can ask to export or delete their portal account data. Review and resolve each
            one here — every action is written to the audit trail.
          </p>
        </div>
        <div className="flex gap-1.5">
          {['pending', 'completed', 'rejected', 'all'].map((s) => (
            <Button key={s} size="sm" variant={status === s ? 'default' : 'outline'}
              onClick={() => setStatus(s)} data-testid={`privacy-filter-${s}`}>
              {s[0].toUpperCase() + s.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {!rows && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}

      {rows && (
        <div className="grid gap-3">
          {rows.map((r) => (
            <Card key={r.request_id} data-testid={`privacy-request-${r.request_id}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between gap-2">
                  <span>{r.patient_name} · <span className="capitalize">{r.type}</span> request</span>
                  <Badge variant="outline" className={`text-[11px] font-medium ${STATUS_STYLE[r.status] || STATUS_STYLE.pending}`}>
                    {r.status}
                  </Badge>
                </CardTitle>
                <CardDescription>{r.patient_contact} · requested {new Date(r.created_at).toLocaleDateString()}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {r.reason && <p className="text-muted-foreground">“{r.reason}”</p>}
                {r.note && <p className="text-xs text-muted-foreground">Resolution note: {r.note}</p>}
                {r.status === 'pending' && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button size="sm" onClick={() => setActing({ request: r, decision: 'completed' })}
                      data-testid={`privacy-approve-${r.request_id}`}>
                      {r.type === 'delete' ? <Trash2 className="h-3.5 w-3.5 mr-1.5" /> : <FileDown className="h-3.5 w-3.5 mr-1.5" />}
                      {r.type === 'delete' ? 'Confirm & delete' : 'Approve & export'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setActing({ request: r, decision: 'rejected' })}
                      data-testid={`privacy-reject-${r.request_id}`}>Reject</Button>
                  </div>
                )}
                {r.status === 'completed' && r.type === 'export' && (
                  <Button size="sm" variant="outline" onClick={() => downloadExport(r.request_id)}
                    data-testid={`privacy-download-${r.request_id}`}>
                    <FileDown className="h-3.5 w-3.5 mr-1.5" /> Download export
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
          {rows.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-12" data-testid="privacy-requests-empty">
              No {status === 'all' ? '' : status} requests.
            </p>
          )}
        </div>
      )}

      <Dialog open={!!acting} onOpenChange={(v) => !v && setActing(null)}>
        <DialogContent className="max-w-md" data-testid="privacy-resolve-dialog">
          <DialogHeader>
            <DialogTitle>{acting?.decision === 'completed' ? 'Confirm this request' : 'Reject this request'}</DialogTitle>
            <DialogDescription>
              {acting?.decision === 'completed' && acting?.request?.type === 'delete'
                ? "This anonymises the patient's portal account — it can't be undone."
                : acting?.decision === 'completed'
                  ? "This prepares the patient's data for export and marks the request resolved."
                  : 'Tell the patient why this was rejected (optional).'}
            </DialogDescription>
          </DialogHeader>
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Note for the record (optional)" data-testid="privacy-resolve-note" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setActing(null)}>Cancel</Button>
            <Button onClick={resolve} disabled={busy} data-testid="privacy-resolve-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
